import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api, apiError } from '../../lib/api';

export default function OwnerBusinesses() {
  const [list, setList] = useState([]);
  const [err, setErr] = useState('');

  // Search & filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');

  useEffect(() => { (async () => {
    try { const {data} = await api.get('/owner/businesses'); setList(data.businesses||[]); } catch(e){ setErr(apiError(e)); }
  })(); }, []);

  // Live filtered + sorted results
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let result = list.filter((b) => {
      // Status filter
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      // Free-text search across name, category, city, tagline
      if (!term) return true;
      const haystack = [
        b.name,
        b.category_name,
        b.city_name,
        b.tagline,
        b.slug,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'name_desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'views_desc':
          return (b.views || 0) - (a.views || 0);
        case 'views_asc':
          return (a.views || 0) - (b.views || 0);
        case 'status':
          return (a.status || '').localeCompare(b.status || '');
        case 'name_asc':
        default:
          return (a.name || '').localeCompare(b.name || '');
      }
    });

    return result;
  }, [list, searchTerm, statusFilter, sortBy]);

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSortBy('name_asc');
  };

  const hasActiveFilters = searchTerm.trim() !== '' || statusFilter !== 'all' || sortBy !== 'name_asc';

  return (
    <div data-testid="owner-businesses">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h2 className="mb-0">My businesses</h2>
        <Link to="/owner/add" className="btn-ap" data-testid="add-business-link">Add business</Link>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      {/* ===== Search & Filter Bar ===== */}
      <div className="ap-card mb-3" data-testid="owner-business-search">
        <div className="row g-2 align-items-end">
          {/* Search input */}
          <div className="col-md-5">
            <label className="form-label small fw-semibold mb-1">Search</label>
            <div style={{ position: 'relative' }}>
              <svg
                width="16" height="16" viewBox="0 0 16 16" fill="none"
                style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
              >
                <path d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9" stroke="#94a3b8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, category, city…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: 36 }}
                data-testid="owner-business-search-input"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    color: '#94a3b8', fontSize: '1rem', lineHeight: 1
                  }}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Status filter */}
          <div className="col-md-3">
            <label className="form-label small fw-semibold mb-1">Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="owner-business-status-filter"
            >
              <option value="all">All statuses</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Sort */}
          <div className="col-md-3">
            <label className="form-label small fw-semibold mb-1">Sort by</label>
            <select
              className="form-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="owner-business-sort"
            >
              <option value="name_asc">Name (A → Z)</option>
              <option value="name_desc">Name (Z → A)</option>
              <option value="views_desc">Most viewed</option>
              <option value="views_asc">Least viewed</option>
              <option value="status">Status</option>
            </select>
          </div>

          {/* Reset */}
          <div className="col-md-1">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary w-100"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              title="Reset filters"
              data-testid="owner-business-reset"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Result count */}
        <div className="small text-muted-2 mt-2" data-testid="owner-business-count">
          Showing <strong className="text-dark">{filtered.length}</strong> of <strong className="text-dark">{list.length}</strong> business{list.length === 1 ? '' : 'es'}
          {hasActiveFilters && (
            <span> · <span style={{ color: '#4338CA' }}>Filters active</span></span>
          )}
        </div>
      </div>

      {/* ===== Results Grid ===== */}
      <div className="row g-3">
        {filtered.map(b => (
          <div className="col-md-6" key={b.id}>
            <div className="ap-card">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="fw-bold">{b.name}</div>
                  <div className="small text-muted-2">{b.category_name} · {b.city_name}</div>
                  <div className="small mt-1">
                    <span className={'pill pill-'+b.status}>{b.status}</span>{' '}
                    {b.is_premium ? <span className="pill pill-approved ms-1">PREMIUM</span> : null}
                  </div>
                </div>
                <Link to={`/owner/businesses/${b.id}`} className="btn-ap-outline" data-testid={`open-biz-${b.id}`}>Manage</Link>
              </div>
              <div className="d-flex justify-content-between small text-muted-2 mt-3">
                <span>Views: <strong className="text-dark">{b.views}</strong></span>
                <a href={`/business/${b.slug}`}>Public page →</a>
              </div>
            </div>
          </div>
        ))}

        {/* No businesses at all */}
        {list.length === 0 && (
          <div className="col-12">
            <div className="ap-card">No businesses yet. Add one to get started.</div>
          </div>
        )}

        {/* No results for current search/filter */}
        {list.length > 0 && filtered.length === 0 && (
          <div className="col-12" data-testid="owner-business-no-results">
            <div className="ap-card text-center py-4">
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔍</div>
              <div className="fw-semibold mb-1">No businesses match your search</div>
              <div className="small text-muted-2 mb-3">
                Try a different keyword, status, or reset the filters.
              </div>
              <button type="button" className="btn-ap-outline btn-sm" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}